import React, { useCallback, useEffect } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "./ui/dialog";

export default function ProjectImagesLightbox({
  images,
  activeIndex,
  onClose,
  onNavigate,
  title = "Project showcase",
  size = "default",
}) {
  const image = images[activeIndex];
  const canNavigate = images.length > 1;
  const isCompact = size === "compact";

  const goPrev = useCallback(() => {
    if (!canNavigate) return;
    onNavigate((activeIndex - 1 + images.length) % images.length);
  }, [activeIndex, canNavigate, images.length, onNavigate]);

  const goNext = useCallback(() => {
    if (!canNavigate) return;
    onNavigate((activeIndex + 1) % images.length);
  }, [activeIndex, canNavigate, images.length, onNavigate]);

  useEffect(() => {
    if (!image) return undefined;

    const onKeyDown = (e) => {
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
      if (e.key === "Escape") onClose();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [image, goPrev, goNext, onClose]);

  if (!image) return null;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        showClose={false}
        className={`overflow-hidden p-0 gap-0 border-0 bg-[#0a1a2f] text-white ${
          isCompact ? "max-w-3xl max-h-[82vh]" : "max-w-6xl max-h-[95vh]"
        }`}
      >
        <DialogTitle className="sr-only">
          {title} — image {activeIndex + 1}
        </DialogTitle>
        <DialogDescription className="sr-only">
          Project image {activeIndex + 1} of {images.length}
        </DialogDescription>

        <div className="relative bg-black">
          <img
            src={image}
            alt={`${title} — ${activeIndex + 1}`}
            className={`mx-auto w-full object-contain ${
              isCompact ? "max-h-[62vh]" : "max-h-[82vh]"
            }`}
          />

          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="absolute right-3 top-3 z-20 flex h-10 w-10 items-center justify-center rounded-full border border-white/25 bg-black/70 text-white transition-colors hover:bg-black/90"
          >
            <X className="h-5 w-5" />
          </button>

          {canNavigate && (
            <>
              <button
                type="button"
                onClick={goPrev}
                aria-label="Previous image"
                className="absolute left-3 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-black/60 text-white transition-colors hover:bg-[#1e6fcf]"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                type="button"
                onClick={goNext}
                aria-label="Next image"
                className="absolute right-3 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-black/60 text-white transition-colors hover:bg-[#1e6fcf]"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </>
          )}
        </div>

        <div className="flex items-center justify-between gap-4 border-t border-white/10 px-6 py-4">
          <p className="line-clamp-1 text-sm font-semibold text-slate-200">{title}</p>
          <span className="shrink-0 text-xs font-bold text-slate-400">
            {activeIndex + 1} / {images.length}
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
